import { StepperFormProps } from '../../../components/stepper/stepperTypes';
import { CourseToBeScheduled } from '../ScheduleCoursePage';
import {
    BodyRegular,
    BodySmall,
    Card,
    FlexAlignCenter,
    FlexColLg,
    FlexColMd,
    Headline3,
    Headline4,
    Headline5,
} from '../../../components/Elements';
import strings from '../../../strings/strings.json';
import * as React from 'react';
import StepperButtonRow from '../../../components/stepper/StepperButtonRow';
import { logError } from '../../../lib/debug-helpers';
import { APIError } from '../../../lib/_api-helpers';
import useModifyRequest from '../../../hooks/useModifyRequest';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from '../../../theme';
import Button from '../../../components/buttons/Button';
import EditIcon from '../../../assets/icons/controls/EditIcon';
import ScheduleIcon from '../../../assets/icons/pathway/ScheduleIcon';
import TimeIcon from '../../../assets/icons/pathway/TimeIcon';
import LinkIcon from '../../../assets/icons/pathway/LinkIcon';
import Notification, { NotificationType } from '../../../components/notifiers/Notification';
import { MeetingCard, MeetingDetailSection, MeetingHeaderSection, MeetingInfo } from './commonElements';
import { LocationType } from '../../../models/ActivityMeeting';
import LocationIcon from '../../../assets/icons/controls/LocationIcon';
import { IconSize } from '../../../assets/icons/icon-sizes';
import { ScheduleCourseChargeNotifier } from 'components/notifiers/PaymentNotifiers';
import { AuthContext } from 'contextProviders/AuthContext';
import { RoleType } from 'models/User';

type CreateMeetingFields = {
    activityId: string;
    meetingStartDate: Date;
    locationType: LocationType;
    location: string | undefined;
    joinLink: string | undefined;
};

type CreateScheduleRequest = {
    courseId: string;
    courseStartDate: Date;
    courseEndDate: Date;
    createMeetingRequests: CreateMeetingFields[];
    trainerId: string;
    minimumLearners: number | undefined;
    maximumLearners: number | undefined;
};

export default function ReviewScheduleForm(props: StepperFormProps<CourseToBeScheduled>): JSX.Element {
    const { previous, newObject: course, changeStep, steps } = props;
    const { userData } = React.useContext(AuthContext);
    const navigate = useNavigate();

    const [errors, setErrors] = React.useState<APIError[]>([]);

    const { modifyData: createSchedule, loading: tryingToScheduleCourse } = useModifyRequest(
        `courses/${course.courseId}/schedule`,
        'POST',
    );

    async function scheduleCourse() {
        setErrors([]);

        const scheduleMeetingRequests: CreateMeetingFields[] = course.meetings.map((meeting) => {
            const date = new Date( //combine date and time
                (meeting.startDate as Date).getFullYear(),
                (meeting.startDate as Date).getMonth(),
                (meeting.startDate as Date).getDate(),
                (meeting.startTime as Date).getHours(),
                (meeting.startTime as Date).getMinutes(),
            );

            return {
                activityId: meeting.activityId,
                meetingStartDate: date,
                locationType: meeting.locationType ?? LocationType.ONLINE,
                joinLink: meeting.locationType === LocationType.ONLINE ? meeting.joinLink : undefined,
                location: meeting.locationType === LocationType.IN_PERSON ? meeting.location : undefined,
            };
        });

        const requestData: CreateScheduleRequest = {
            courseId: course.courseId,
            courseStartDate: course.courseStartDate as Date,
            courseEndDate: course.courseEndDate as Date,
            createMeetingRequests: scheduleMeetingRequests,
            // eslint-disable-next-line eqeqeq
            trainerId: course.trainerId ?? '',
            minimumLearners: course.minimumLearners,
            maximumLearners: course.maximumLearners,
        };

        const response = await createSchedule(requestData);

        const { value: scheduleId, errors } = response;
        if (errors) {
            logError(errors);
            setErrors(errors);
            return;
        }
        if (scheduleId) {
            if (userData.activeRole === RoleType.ORGANISATION_ADMIN) {
                navigate(`/organisation/dashboard`, {
                    state: { activeTabId: 'liveCourses', activeRowId: scheduleId },
                });
            } else {
                navigate(`/trainer/live-courses`);
            }
            return;
        }
    }

    const loading = tryingToScheduleCourse;
    const organisationIsSubscribed = course.organisationNumberOfLiveCourses >= 2;

    return (
        <FlexColLg>
            <Headline3>{strings.scheduleCourseStepper.reviewScheduleForm.title}</Headline3>
            <BodyRegular>{strings.scheduleCourseStepper.reviewScheduleForm.subtitle}</BodyRegular>
            <DetailCard>
                <Headline4>{course.courseName}</Headline4>
                <div>
                    <DetailRow>
                        <DetailTextSection>
                            <DetailTitle>
                                {strings.scheduleCourseStepper.reviewScheduleForm.startDateLabel}
                            </DetailTitle>
                            <MainDetail $bold>{course.courseStartDate?.toLocaleDateString()}</MainDetail>
                        </DetailTextSection>
                        <Button
                            title={strings.scheduleCourseStepper.reviewScheduleForm.editButton}
                            aria={strings.scheduleCourseStepper.reviewScheduleForm.startDateEditButtonAria}
                            icon={<EditIcon />}
                            onClick={() => {
                                changeStep(0);
                            }}
                            disabled={loading}
                            fullwidth={false}
                        />
                    </DetailRow>
                    <DetailRow>
                        <DetailTextSection>
                            <DetailTitle>{strings.scheduleCourseStepper.reviewScheduleForm.endDateLabel}</DetailTitle>
                            <MainDetail $bold>
                                {course.courseEndDate?.toLocaleDateString() ??
                                    strings.scheduleCourseStepper.reviewScheduleForm.noDateSelected}
                            </MainDetail>
                        </DetailTextSection>
                        <Button
                            title={strings.scheduleCourseStepper.reviewScheduleForm.editButton}
                            aria={strings.scheduleCourseStepper.reviewScheduleForm.endDateEditButtonAria}
                            icon={<EditIcon />}
                            onClick={() => {
                                changeStep(0);
                            }}
                            disabled={loading}
                            fullwidth={false}
                        />
                    </DetailRow>
                    <DetailRow>
                        <DetailTextSection>
                            <DetailTitle>
                                {strings.scheduleCourseStepper.reviewScheduleForm.numberOfLearnersLabel}
                            </DetailTitle>
                            <MainDetail $bold>
                                {course.minimumLearners}
                                {strings.scheduleCourseStepper.reviewScheduleForm.numberOfLearnersSeparator}
                                {course.maximumLearners}
                            </MainDetail>
                        </DetailTextSection>

                        <Button
                            title={strings.scheduleCourseStepper.reviewScheduleForm.editButton}
                            aria={strings.scheduleCourseStepper.reviewScheduleForm.numberOfLearnersEditButtonAria}
                            icon={<EditIcon />}
                            onClick={() => {
                                changeStep(
                                    steps.findIndex((step) => {
                                        return step.name === 'Learners';
                                    }),
                                );
                            }}
                            disabled={loading}
                            fullwidth={false}
                        />
                    </DetailRow>
                    <DetailRow>
                        <DetailTextSection>
                            <DetailTitle>{strings.scheduleCourseStepper.reviewScheduleForm.trainerLabel}</DetailTitle>
                            <MainDetail $bold>{course.trainerName}</MainDetail>
                        </DetailTextSection>
                        {steps.some((step) => step.name === 'Trainer') && (
                            <>
                                <Button
                                    title={strings.scheduleCourseStepper.reviewScheduleForm.editButton}
                                    aria={strings.scheduleCourseStepper.reviewScheduleForm.trainerEditButtonAria}
                                    icon={<EditIcon />}
                                    onClick={() => {
                                        changeStep(
                                            steps.findIndex((step) => {
                                                return step.name === 'Trainer';
                                            }),
                                        );
                                    }}
                                    disabled={loading}
                                    fullwidth={false}
                                />
                            </>
                        )}
                    </DetailRow>
                </div>
            </DetailCard>
            {course.meetings.length === 0 ? null : (
                <>
                    <Headline4>{strings.scheduleCourseStepper.reviewScheduleForm.meetingsTitle}</Headline4>
                    <MeetingsSection>
                        {course.meetings.map((meeting) => {
                            const meetingStartTimeString = meeting.startTime
                                ?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h12' })
                                .replaceAll(' ', '');

                            const meetingEndTime = meeting.startTime
                                ? new Date(meeting.startTime.getTime() + 60000 * (meeting.durationInMins ?? 1))
                                : '';

                            const meetingEndTimeString = meetingEndTime
                                ? meetingEndTime
                                      .toLocaleTimeString('en-GB', {
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          hourCycle: 'h12',
                                      })
                                      .replaceAll(' ', '')
                                : '';

                            return (
                                <MeetingCard>
                                    <MeetingHeaderSection>
                                        <Headline5>{meeting.title}</Headline5>
                                        <Button
                                            title={strings.scheduleCourseStepper.reviewScheduleForm.editButton}
                                            aria={
                                                strings.scheduleCourseStepper.reviewScheduleForm.meetingEditButtonAria
                                            }
                                            icon={<EditIcon />}
                                            onClick={() => {
                                                changeStep(steps.length - 2);
                                            }}
                                            disabled={loading}
                                        />
                                    </MeetingHeaderSection>
                                    <MeetingDetailSection>
                                        <MeetingInfo>
                                            <ScheduleIcon />
                                            <BodySmall>{meeting.startDate?.toLocaleDateString()}</BodySmall>
                                        </MeetingInfo>
                                        <MeetingInfo>
                                            <TimeIcon />
                                            <BodySmall>
                                                {meetingStartTimeString + ' - ' + meetingEndTimeString}
                                            </BodySmall>
                                        </MeetingInfo>
                                    </MeetingDetailSection>
                                    {meeting.locationType === LocationType.ONLINE && (
                                        <MeetingInfo>
                                            <LinkIcon />
                                            <BodySmall>{meeting.joinLink}</BodySmall>
                                        </MeetingInfo>
                                    )}
                                    {meeting.locationType === LocationType.IN_PERSON && (
                                        <MeetingInfo>
                                            <LocationIcon size={IconSize.MEDIUM} />
                                            <BodySmall>{meeting.location}</BodySmall>
                                        </MeetingInfo>
                                    )}
                                </MeetingCard>
                            );
                        })}
                    </MeetingsSection>
                </>
            )}
            {!course.courseIsFree && (
                <ScheduleCourseChargeNotifier organisationIsSubscribed={organisationIsSubscribed} />
            )}
            {errors.length > 0 && !tryingToScheduleCourse && (
                <Notification
                    notificationType={NotificationType.DANGER}
                    title={strings.scheduleCourseStepper.reviewScheduleForm.scheduleCourseErrorTitle}
                    description={strings.scheduleCourseStepper.reviewScheduleForm.scheduleCourseErrorDescription}
                    maxWidth={'100%'}
                />
            )}
            <StepperButtonRow
                submitButtonTitle={strings.scheduleCourseStepper.reviewScheduleForm.submitButton}
                submitButtonAria={strings.scheduleCourseStepper.reviewScheduleForm.submitButtonAria}
                submitButtonFunction={scheduleCourse}
                previousButtonFunction={previous}
                submitting={loading}
            />
        </FlexColLg>
    );
}

const MeetingsSection = styled(FlexColMd)`
    background: ${theme.cardSecondaryBackgroundColour};
    border-radius: ${sizes.borderRadiusMd};
    padding: ${sizes.spacingMd};
`;

const MainDetail = styled(BodyRegular)`
    flex-grow: 1;
    @media (max-width: ${breakpoints.sm}) {
        flex-grow: 0;
    }
`;

const DetailTitle = styled(BodyRegular)`
    min-width: 200px;
    @media (max-width: ${breakpoints.sm}) {
        min-width: 0;
    }
`;

const DetailTextSection = styled(FlexAlignCenter)`
    flex-grow: 1;
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: ${sizes.spacingXs};
        align-items: flex-start;
        flex-grow: 0;
    }
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${sizes.spacingMd};
    border-top: 1px solid ${theme.cardBorder};
`;

const DetailCard = styled(Card)`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingLg};
`;
